import { partnerRouteMatcher } from './partner/partner-route.matcher';
import { Routes, Router } from '@angular/router';
import { inject } from '@angular/core';
import { ArticleResolver } from './blog/article.resolver';
import { exploreCityResolver } from './explore-city/explore-city.resolver';
import { exploreCountryResolver } from './explore-country/explore-country.resolver';
import { exploreHostelsResolver } from './explore-hostels/explore-hostels.resolver';
import { blockedCountryGuard } from './guards/blocked-country.guard';
import { hostelsByCityResolver } from './hostels-by-city.resolver';
import { MainComponent } from './main/main.component';
import { MobileExplorePageComponent } from './mobile-explore-page/mobile-explore-page.component';
import { PartnersPageComponent } from './partners/partners-page.component';
import { SitemapComponent } from './sitemap/sitemap.component';
import { StoreRedirectComponent } from './store-redirect/store-redirect.component';

export const routes: Routes = [
  {
    path: '',
    component: MainComponent,
    data: {
      title: 'Hostel Hop - Discover the best hostels worldwide',
      name: 'index',
      metaTags: {
        title: 'Hostel Hop - Discover the best hostels worldwide',
        description:
          'Discover the best hostels worldwide with Hostel Hop! Explore budget-friendly accommodations, travel tips, and unforgettable backpacking experiences. Start your adventure today!',
      },
    },
  },
  {
    path: 'explore',
    component: MobileExplorePageComponent,
    data: {
      title: 'Hostel Hop - Discover the best hostels worldwide',
      name: 'explore',
      mobile: true,
      showFooter: false,
      metaTags: {
        title: 'Hostel Hop - Discover the best hostels worldwide',
        description:
          'Discover the best hostels worldwide with Hostel Hop! Explore budget-friendly accommodations, travel tips, and unforgettable backpacking experiences. Start your adventure today!',
      },
    },
  },
  {
    path: 'sitemap',
    loadComponent: () =>
      import('./sitemap/sitemap.component').then((m) => SitemapComponent),
    data: {
      title: 'Sitemap - Hostel Hop',
      name: 'sitemap',
      mobile: true,
      showFooter: true,
      metaTags: {
        title: 'Sitemap - Hostel Hop',
        description: 'Sitemap for Hostel Hop',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
        'twitter:title': 'Sitemap - Hostel Hop',
      },
    },
  },
  {
    path: 'hostel/:id',
    redirectTo: 'hostels/:id',
    pathMatch: 'full',
  },
  {
    path: 'hostels',
    loadChildren: () =>
      import('./hostel/hostel-page.module').then((m) => m.HostelPageModule),
    // data: {
    //   title: 'Hostel Hop',
    //   showFooter: false,
    //   metaTags: {
    //     title: 'Hostel Hop',
    //     description: "Discover the best hostels worldwide with Hostel Hop! Explore budget-friendly accommodations, travel tips, and unforgettable backpacking experiences. Start your adventure today!",
    //   }
    // }
  },
  {
    path: 'explore-countries',
    children: [
      {
        path: ':country',
        loadComponent: () =>
          import('./explore-country/explore-country.component').then(
            (m) => m.ExploreCountryComponent
          ),
        resolve: {
          exploreCountriesData: exploreCountryResolver, // Optional resolver
        },
      },
    ],
  },
  {
    path: 'explore-city',
    children: [
      {
        path: ':city',
        loadComponent: () =>
          import('./explore-city/explore-city.component').then(
            (m) => m.ExploreCityComponent
          ),
        resolve: {
          exploreCityData: exploreCityResolver, // Optional resolver
        },
      },
    ],
  },
  {
    path: 'city/:slug',
    redirectTo: 'cities/:id',
    pathMatch: 'full',
  },
  {
    path: 'cities',
    data: {
      showFooter: true,
      showMobileHeader: true,
    },
    children: [
      {
        path: ':slug',
        canActivate: [blockedCountryGuard],
        loadComponent: () =>
          import('./city-stays/city-stays.component').then(
            (m) => m.CityStaysComponent
          ),
        resolve: {
          hostels: hostelsByCityResolver,
        },
        data: {
          title:
            'Discover hostels in [[SSR_DATA_CITY_NAME]], [[SSR_DATA_COUNTRY_OR_US_STATE_NAME]] - Hostel Hop',
          name: 'city',
          metaTags: {
            name: '[[SSR_DATA_CITY_NAME]], [[SSR_DATA_COUNTRY_OR_US_STATE_NAME]] - Discover the best hostels in the city - Hostel Hop',

            title:
              'Discover hostels in [[SSR_DATA_CITY_NAME]], [[SSR_DATA_COUNTRY_OR_US_STATE_NAME]] - Hostel Hop',
            'og:title':
              '[[SSR_DATA_CITY_NAME]], [[SSR_DATA_COUNTRY_OR_US_STATE_NAME]] - Discover the best hostels in the city - Hostel Hop',
            'twitter:title':
              '[[SSR_DATA_CITY_NAME]], [[SSR_DATA_COUNTRY_OR_US_STATE_NAME]] - Discover the best hostels in the city - Hostel Hop',

            'og:image':
              'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
            'twitter:image':
              'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',

            description:
              'Discover hostels in [[SSR_DATA_CITY_NAME]], [[SSR_DATA_COUNTRY_OR_US_STATE_NAME]]',
            'og:description':
              'Discover hostels in [[SSR_DATA_CITY_NAME]], [[SSR_DATA_COUNTRY_OR_US_STATE_NAME]]',
            'twitter:description':
              'Discover hostels in [[SSR_DATA_CITY_NAME]], [[SSR_DATA_COUNTRY_OR_US_STATE_NAME]]',
            'twitter:card':
              'Discover hostels in [[SSR_DATA_CITY_NAME]], [[SSR_DATA_COUNTRY_OR_US_STATE_NAME]]',

            'og:type': 'website',
          },
        },
      },
    ],
  },
  {
    path: 'booking',
    loadChildren: () =>
      import('./booking/booking.module').then((m) => m.BookingModule),
  },
  {
    path: 'explore-hostels',
    loadComponent: () =>
      import('./explore-hostels/explore-hostels.component').then(
        (m) => m.ExploreHostelsComponent
      ),
    resolve: {
      exploreHostelsData: exploreHostelsResolver,
    },
  },
  {
    path: 'filters',
    loadChildren: () =>
      import('./filters-mobile/filters-mobile.module').then(
        (m) => m.FiltersMobileModule
      ),
  },
  {
    path: 'help',
    loadComponent: () =>
      import('./help/help.component').then((m) => m.HelpComponent),
    data: {
      title: 'Help',
      name: 'help',
      metaTags: {
        name: 'Help - Hostel Hop',
        title: 'Help - Hostel Hop',
        description: 'Help - Hostel Hop',
        'twitter:title': 'Help - Hostel Hop',
        'twitter:description': 'Help - Hostel Hop',
        'twitter:card': 'Help - Hostel Hop',
        'og:title': 'Help - Hostel Hop',
        'og:description': 'Help - Hostel Hop',
        'og:type': 'website',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  {
    path: 'blog',
    loadComponent: () =>
      import('./blog/blog.component').then((m) => m.BlogComponent),
    data: {
      title: 'Blog - Hostel Hop',
      name: 'blog',
      metaTags: {
        name: 'Blog - Hostel Hop',
        title: 'Blog - Hostel Hop',
        description: 'Blog - Hostel Hop',
        'twitter:title': 'Blog - Hostel Hop',
        'twitter:description': 'Blog - Hostel Hop',
        'twitter:card': 'Blog - Hostel Hop',
        'og:title': 'Blog - Hostel Hop',
        'og:description': 'Blog - Hostel Hop',
        'og:type': 'blog',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  {
    path: 'blog/:slug',
    loadComponent: () =>
      import('./blog/blog-article/blog-article.component').then(
        (m) => m.BlogArticleComponent
      ),
    resolve: {
      article: ArticleResolver,
    },
    data: {
      title: 'Blog - Hostel Hop',
      name: 'blog-article',
      'og:image':
        'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
    },
  },
  {
    path: 'about-us',
    loadComponent: () =>
      import('./about-us/about-us.component').then((m) => m.AboutUsComponent),
    data: {
      title: 'About Us',
      name: 'about-us',
      metaTags: {
        name: 'About Us',
        title: 'About Us',
        description: 'About Hostel Hop',
        'twitter:title': 'About Us - Hostel Hop',
        'twitter:description': 'About Us - Hostel Hop',
        'twitter:card': 'About Us - Hostel Hop',
        'og:title': 'About Us - Hostel Hop',
        'og:description': 'About Hostel Hop',
        'og:type': 'website',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  {
    path: 'careers',
    loadComponent: () =>
      import('./careers/careers.component').then((m) => m.CareersComponent),
    data: {
      title: 'Careers - Hostel Hop',
      name: 'careers',
      metaTags: {
        name: 'Careers - Hostel Hop',
        title: 'Careers - Hostel Hop',
        description: 'Careers at Hostel Hop',
        'twitter:title': 'Careers - Hostel Hop',
        'twitter:description': 'Careers - Hostel Hop',
        'twitter:card': 'Careers - Hostel Hop',
        'og:title': 'Careers - Hostel Hop',
        'og:description': 'Careers at Hostel Hop',
        'og:type': 'website',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  {
    path: 'partners',
    component: PartnersPageComponent,
    data: {
      title: 'Our Partners - Hostel Hop',
      name: 'partners',
      metaTags: {
        name: 'Our Partners - Hostel Hop',
        title: 'Our Partners - Hostel Hop',
        description: 'Discover our trusted partners who make travel easier, cheaper, and more rewarding for our community.',
        'twitter:title': 'Our Partners - Hostel Hop',
        'twitter:description': 'Discover our trusted partners who make travel easier, cheaper, and more rewarding for our community.',
        'twitter:card': 'Our Partners - Hostel Hop',
        'og:title': 'Our Partners - Hostel Hop',
        'og:description': 'Discover our trusted partners who make travel easier, cheaper, and more rewarding for our community.',
        'og:type': 'website',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  {
    path: 'giveaway',
    loadComponent: () =>
      import('./giveaway/giveaway-page.component').then((m) => m.GiveawayPageComponent),
    data: {
      title: 'HostelHop Monthly Giveaway - Hostel Hop',
      name: 'giveaway',
      metaTags: {
        name: 'HostelHop Monthly Giveaway - Hostel Hop',
        title: 'HostelHop Monthly Giveaway - Hostel Hop',
        description: 'Every month, HostelHop rewards our community with amazing prizes—free nights, travel credits, and partner perks. Enter now!',
        'twitter:title': 'HostelHop Monthly Giveaway - Hostel Hop',
        'twitter:description': 'Every month, HostelHop rewards our community with amazing prizes—free nights, travel credits, and partner perks. Enter now!',
        'twitter:card': 'HostelHop Monthly Giveaway - Hostel Hop',
        'og:title': 'HostelHop Monthly Giveaway - Hostel Hop',
        'og:description': 'Every month, HostelHop rewards our community with amazing prizes—free nights, travel credits, and partner perks. Enter now!',
        'og:type': 'website',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  {
    path: 'affiliate-program',
    loadComponent: () =>
      import('./affiliate-program/affiliate-program.component').then(
        (m) => m.AffiliateProgramComponent
      ),
    data: {
      title: 'Affiliate Program',
      name: 'affiliate-program',
      metaTags: {
        name: 'Affiliate Program - Hostel Hop',
        title: 'Affiliate Program - Hostel Hop',
        description: 'Join the Hostel Hop Affiliate Program',
        'twitter:title': 'Affiliate Program - Hostel Hop',
        'twitter:description': 'Affiliate Program - Hostel Hop',
        'twitter:card': 'Affiliate Program - Hostel Hop',
        'og:title': 'Affiliate Program - Hostel Hop',
        'og:description': 'Join the Hostel Hop Affiliate Program',
        'og:type': 'website',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  {
    path: 'ambassador-program',
    loadComponent: () =>
      import('./ambassador-program/ambassador-program.component').then(
        (m) => m.AmbassadorProgramComponent
      ),
    data: {
      title: 'Ambassador Program',
      name: 'ambassador-program',
      metaTags: {
        name: 'Ambassador Program - Hostel Hop',
        title: 'Ambassador Program - Hostel Hop',
        description: 'Join the Hostel Hop Ambassador Program',
        'twitter:title': 'Ambassador Program - Hostel Hop',
        'twitter:description': 'Ambassador Program - Hostel Hop',
        'twitter:card': 'Ambassador Program - Hostel Hop',
        'og:title': 'Ambassador Program - Hostel Hop',
        'og:description': 'Join the Hostel Hop Ambassador Program',
        'og:type': 'website',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  {
    path: 'hostel-signup',
    loadComponent: () =>
      import('./hostel-signup/hostel-signup.component').then(
        (m) => m.HostelSignupComponent
      ),
    data: {
      title: 'Hostel Signup - Hostel Hop',
      name: 'hostel-signup',
      metaTags: {
        name: 'Hostel Signup - Hostel Hop',
        title: 'Hostel Signup - Hostel Hop',
        description: 'Hostel Hop Hostel Signup',
        'twitter:title': 'Hostel Signup - Hostel Hop',
        'twitter:description': 'Hostel Signup - Hostel Hop',
        'twitter:card': 'Hostel Signup - Hostel Hop',
        'og:title': 'Hostel Signup - Hostel Hop',
        'og:description': 'Hostel Hop Hostel Signup',
        'og:type': 'website',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  {
    path: 'terms-of-service',
    loadComponent: () =>
      import('./terms-of-service/terms-of-service.component').then(
        (m) => m.TermsOfServiceComponent
      ),
    data: {
      title: 'Terms of Service - Hostel Hop',
      name: 'terms-of-service',
      metaTags: {
        name: 'Terms of Service - Hostel Hop',
        title: 'Terms of Service - Hostel Hop',
        description: 'Hostel Hop Terms of Service',
        'twitter:title': 'Terms of Service - Hostel Hop',
        'twitter:description': 'Terms of Service - Hostel Hop',
        'twitter:card': 'Terms of Service - Hostel Hop',
        'og:title': 'Terms of Service - Hostel Hop',
        'og:description': 'Hostel Hop Terms of Service',
        'og:type': 'website',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  {
    path: 'privacy-policy',
    loadComponent: () =>
      import('./privacy-policy/privacy-policy.component').then(
        (m) => m.PrivacyPolicyComponent
      ),
    data: {
      title: 'Privacy Policy',
      name: 'privacy-policy',
      showPromotion: false,
      metaTags: {
        name: 'Privacy Policy - Hostel Hop',
        title: 'Privacy Policy - Hostel Hop',
        description: 'Hostel Hop Privacy Policy',
        'twitter:title': 'Privacy Policy - Hostel Hop',
        'twitter:description': 'Privacy Policy',
        'twitter:card': 'Privacy Policy',
        'og:title': 'Privacy Policy - Hostel Hop',
        'og:description': 'Hostel Hop Privacy Policy',
        'og:type': 'website',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  {
    path: 'cookie-policy',
    loadComponent: () =>
      import('./cookie-policy/cookie-policy.component').then(
        (m) => m.CookiePolicyComponent
      ),
    data: {
      title: 'Cookie Policy',
      name: 'cookie-policy',
      metaTags: {
        name: 'Cookie Policy - Hostel Hop',
        title: 'Cookie Policy - Hostel Hop',
        description: 'Hostel Hop Cookie Policy',
        'twitter:title': 'Cookie Policy - Hostel Hop',
        'twitter:description': 'Hostel Hop Cookie Policy',
        'twitter:card': 'Hostel Hop Cookie Policy',
        'og:title': 'Cookie Policy - Hostel Hop',
        'og:description': 'Hostel Hop Cookie Policy',
        'og:type': 'website',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  {
    path: 'rewards',
    loadComponent: () =>
      import('./rewards/rewards.component').then(
        (m) => m.RewardsComponent
      ),
    data: {
      title: 'HostelHop Rewards - Travel Rewards Program',
      name: 'rewards',
      metaTags: {
        name: 'HostelHop Rewards - Travel Rewards Program',
        title: 'HostelHop Rewards - Travel Rewards Program',
        description:
          'Earn $25 signup credit, 4% referral cashback, ambassador perks, monthly giveaways, and partner discounts. Join HostelHop\'s rewards program and travel smarter.',
        'twitter:title': 'HostelHop Rewards - Travel Rewards Program',
        'twitter:description':
          'Earn $25 signup credit, 4% referral cashback, ambassador perks, monthly giveaways, and partner discounts. Join HostelHop\'s rewards program and travel smarter.',
        'twitter:card': 'HostelHop Rewards - Travel Rewards Program',
        'og:title': 'HostelHop Rewards - Travel Rewards Program',
        'og:description':
          'Earn $25 signup credit, 4% referral cashback, ambassador perks, monthly giveaways, and partner discounts. Join HostelHop\'s rewards program and travel smarter.',
        'og:type': 'website',
        'og:image':
          'https://storage.googleapis.com/hostel-hop-storage/app/prod/logo_h.png',
      },
    },
  },
  // make sure download/app-store-redirect works
  { path: 'app-store-redirect', component: StoreRedirectComponent },
  { path: 'download', component: StoreRedirectComponent },

  {
    path: 'plan-your-trip', pathMatch: 'full', component: MainComponent,
    canActivate: [(route) => inject(Router).createUrlTree(['/'], {queryParams: {...route.queryParams,trip:'1'}})],
  },
  {
    path: 'plan-your-trip',
    loadComponent: () => import('./trips/trip-shell.component').then(m=>m.TripShellComponent),
    data: {showHeader:false,showFooter:true,showMobileHeader:true,showPromotion:false,title:'Plan your trip — Hostel Hop'},
    children: [
      {path:'',loadComponent:()=>import('./trips/trip-planner.component').then(m=>m.TripPlannerComponent)},
      {path:'checkout',loadComponent:()=>import('./trips/trip-checkout.component').then(m=>m.TripCheckoutComponent)},
      {path:'confirmed/:code',loadComponent:()=>import('./trips/trip-confirmation.component').then(m=>m.TripConfirmationComponent)},
    ],
  },
  {path:'multileg',redirectTo:'plan-your-trip',pathMatch:'full'},

  {
    matcher: partnerRouteMatcher,
    loadChildren: () => import('./partner/partner.module').then(m => m.PartnerModule),
    data: { showFooter: false, showHeader: false, showMobileHeader: false, showPromotion: false },
  },

  { path: '**', redirectTo: '' },
];
